/**
 * Small formatting helpers for the Profile UI (dates, plus the iCalendar TEXT
 * escaper at the bottom).
 *
 * ProfileView.tsx (the hosted-widget original this was ported from) uses
 * `moment` throughout, but `moment` isn't a dependency of this app and the
 * rest of `apps/webcomponents` already formats dates with native
 * `Intl`/`toLocaleDateString` (see ReviewCard.tsx, SessionCard.tsx,
 * TimetableFilters.tsx) — this file follows that convention instead of
 * adding a new date library.
 */

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "5 Mar 2026" — mirrors the house style used elsewhere in this app. */
export function formatDateShort(value: string | null | undefined): string {
  const d = toDate(value);
  if (!d) return "-";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** "5 March 2026" — mirrors moment's "Do MMMM YYYY" minus the ordinal suffix. */
export function formatDateLong(value: string | null | undefined): string {
  const d = toDate(value);
  if (!d) return "-";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

/** "March 2026" — used for "Member since". */
export function formatMonthYear(value: string | null | undefined): string {
  const d = toDate(value);
  if (!d) return "";
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

/** "14:30" — 24-hour, per this repo's UI convention (no AM/PM). */
export function formatTime(value: string | null | undefined): string {
  const d = toDate(value);
  if (!d) return "";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** "Mon, 5 Mar 2026" */
export function formatDateWithWeekday(value: string | null | undefined): string {
  const d = toDate(value);
  if (!d) return "-";
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 1000 * 60 * 60 * 24 * 365],
  ["month", 1000 * 60 * 60 * 24 * 30],
  ["week", 1000 * 60 * 60 * 24 * 7],
  ["day", 1000 * 60 * 60 * 24],
  ["hour", 1000 * 60 * 60],
  ["minute", 1000 * 60],
];

const relativeFormatter = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });

/** "in 3 days" / "2 hours ago" — mirrors moment's `.fromNow()`. */
export function formatRelative(value: string | null | undefined): string {
  const d = toDate(value);
  if (!d) return "";
  const diffMs = d.getTime() - Date.now();
  for (const [unit, ms] of RELATIVE_UNITS) {
    if (Math.abs(diffMs) >= ms || unit === "minute") {
      return relativeFormatter.format(Math.round(diffMs / ms), unit);
    }
  }
  return relativeFormatter.format(0, "minute");
}

/**
 * Escape a value for an iCalendar TEXT property value (RFC 5545 §3.3.11).
 *
 * iCalendar is line-based: a property ends at the line break, so a raw newline
 * inside a value does not produce a multi-line SUMMARY — it ends SUMMARY and
 * makes whatever follows a NEW property. A listing titled
 * `Yoga\nEND:VEVENT\nBEGIN:VEVENT\nSUMMARY:...` therefore injects extra events
 * into the customer's downloaded `.ics` and, once imported, into their
 * calendar. `\`, `;` and `,` are the value's own delimiters and corrupt it in
 * the quieter way (a title with a comma splits into two values).
 *
 * The backslash rule runs first on purpose: escaping it after the others would
 * double-escape the backslashes those rules just added.
 *
 * Sibling note: `generateGoogleCalendarUrl` needs none of this — it puts the
 * same field through `encodeURIComponent`, which is the equivalent guard for a
 * query string.
 */
export function escapeICalText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r\n|\r|\n/g, "\\n");
}
